import { useSentinelPhase } from '@hooks/useSentinelPhase';
import { FormattedMessage, FormattedTime } from 'react-intl';
import type { TimelineEvent } from 'src/types/sentinel';

import './Timeline.css';

interface TimelinePhaseEventProps {
	event: TimelineEvent;
	isActive: boolean;
}

function TimelinePhaseEvent({ event, isActive }: TimelinePhaseEventProps) {
	let iconPhase = '';
	if (event.eventType === 'phase_start') iconPhase = 'radar';
	if (event.eventType === 'phase_complete') iconPhase = 'check-circle';

	return (
		<li className={`timeline-event phase ${isActive ? 'active' : ''}`}>
			<span id={`timeline-${event.eventType}-${event.phase}`} className={`timeline-${event.eventType}`}>
				<span className={`icon icon-${iconPhase}`} />
				<strong>
					<FormattedMessage id={`sentinel.timeline.${event.eventType}.${event.phase}`} />
				</strong>
			</span>
			<small>
				<FormattedTime value={event.timestamp} />
			</small>
		</li>
	);
}

interface TimelineStepEventProps {
	event: TimelineEvent;
}

function TimelineStepEvent({ event }: TimelineStepEventProps) {
	const isActive = event.stepStatus === 'in_progress';

	let iconStatus = 'check';
	if (event.stepStatus === 'pending') iconStatus = 'points';
	if (event.stepStatus === 'failed') iconStatus = 'cross';

	return (
		<li className={`timeline-event step ${isActive ? 'active' : ''}`}>
			<span className={`status-${event.stepStatus}`}>
				<span className={`icon icon-${iconStatus}`} />
				<span>
					<FormattedMessage id={`sentinel.timeline.step.${event.stepId}`} />
					{event.message && <span className="step-message">{event.message}</span>}
				</span>
			</span>
			<small>
				<FormattedTime value={event.timestamp} />
			</small>
		</li>
	);
}

interface TimelineErrorEventProps {
	event: TimelineEvent;
}

function TimelineErrorEvent({ event }: TimelineErrorEventProps) {
	return (
		<li className="timeline-event step error">
			<span>
				<span className="icon icon-cross" />
				{event.message}
			</span>
			<br />
			<small>
				<FormattedTime value={event.timestamp} />
			</small>
		</li>
	);
}

export default function Timeline() {
	const { sessionTimeline, isPhaseRunning } = useSentinelPhase();

	if (sessionTimeline.length === 0) return null;

	const isPhaseEventActive = (event: TimelineEvent): boolean => {
		if (event.eventType === 'phase_complete') return false;
		if (event.eventType === 'phase_start') {
			return isPhaseRunning(event.phase);
		}
		return false;
	};

	return (
		<div id="sidebar-timeline">
			<ul className="timeline-list">
				{sessionTimeline.map((event) => {
					const uniqueKey = event.id || `${event.phase}-${event.eventType}-${event.timestamp}`;
					if (event.eventType === 'phase_start' || event.eventType === 'phase_complete') {
						return <TimelinePhaseEvent key={uniqueKey} event={event} isActive={isPhaseEventActive(event)} />;
					}
					if (event.eventType === 'step_update' && event.stepId && event.stepStatus) {
						return <TimelineStepEvent key={uniqueKey} event={event} />;
					}
					if (event.eventType === 'error' && event.message) {
						return <TimelineErrorEvent key={uniqueKey} event={event} />;
					}
					return null;
				})}
			</ul>
		</div>
	);
}
